import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../config/axios';
import Navbar from '../components/Navbar';
import UserManagement from '../components/UserManagement';
import InventoryManagement from '../components/InventoryManagement';
import SiteManagement from '../components/SiteManagement';
import ArchivedCounts from '../components/ArchivedCounts';
import Reports from '../components/Reports';
import ImportSection from '../components/ImportSection';
import AuditLog from '../components/AuditLog';
import ChatModal from '../components/ChatModal';
import ChatBell from '../components/ChatBell';
import SupportModal from '../components/SupportModal';
import MissingItemTicketsPanel from '../components/MissingItemTicketsPanel';
import { ChevronRight, ChevronDown, Sun, Moon, LogOut, Send, Loader2, PackageMinus } from 'lucide-react';
import '../components/AdminShell.css';
import './Dashboard.css';
import { EXPORT_FAILED_MESSAGE, getFriendlyErrorMessage } from '../utils/errorMessages';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [sites, setSites] = useState([]);
  
  // Load activeTab from localStorage or default to 'inventory'
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('adminDashboard_activeTab');
    if (savedTab === 'audit') return 'inventory'; // role checked after user loads
    // Legacy sidebar entry moved into Reports tabs
    if (savedTab === 'full-inventory') return 'reports';
    return savedTab || 'inventory';
  });
  
  const [loading, setLoading] = useState(true);

  // Load selectedBrand from localStorage or default to null
  const [selectedBrand, setSelectedBrand] = useState(() => {
    const savedBrand = localStorage.getItem('adminDashboard_selectedBrand');
    return savedBrand || null;
  });
  
  const [brands, setBrands] = useState([]);
  const [allInventory, setAllInventory] = useState([]);
  const [archiving, setArchiving] = useState(false);
  const [archiveMessage, setArchiveMessage] = useState('');
  const [financeExporting, setFinanceExporting] = useState(false);
  const [submissionStats, setSubmissionStats] = useState({
    sitesSubmitted: 0,
    sitesPendingReview: 0,
    sitesNotSubmittedCount: 0,
    totalSites: 0,
    periodSites: [],
    notSubmittedSites: [],
    reminderCooldownUntil: null
  });
  const [showNotSubmittedPopup, setShowNotSubmittedPopup] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');
  const [meOpen, setMeOpen] = useState(false);
  const [showPeriodNameModal, setShowPeriodNameModal] = useState(false);
  const [periodNameInput, setPeriodNameInput] = useState('');
  const [showPendingReviewBlockModal, setShowPendingReviewBlockModal] = useState(false);
  const [pendingReviewBlock, setPendingReviewBlock] = useState({ pendingBatchCount: 0, pendingBatches: [] });
  const [checkingPendingReviews, setCheckingPendingReviews] = useState(false);
  const [pendingReviewRemindMessage, setPendingReviewRemindMessage] = useState('');
  const [remindingSiteId, setRemindingSiteId] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [theme, setTheme] = useState(() => { if (typeof window !== 'undefined') { const saved = localStorage.getItem('theme'); if (saved === 'dark') return 'dark'; } return 'light'; });
  const [chatConversationId, setChatConversationId] = useState(null);
  const [chatOtherPartyDisplayName, setChatOtherPartyDisplayName] = useState('');
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatRefreshTrigger, setChatRefreshTrigger] = useState(0);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [missingItemOpenCount, setMissingItemOpenCount] = useState(0);
  const [showMissingItemAlert, setShowMissingItemAlert] = useState(false);
  const [missingItemAlertDismissed, setMissingItemAlertDismissed] = useState(false);
  const [showMissingItemTicketsModal, setShowMissingItemTicketsModal] = useState(false);
  const [missingItemTickets, setMissingItemTickets] = useState([]);
  const [missingItemTicketsLoading, setMissingItemTicketsLoading] = useState(false);
  const [missingItemStatusFilter, setMissingItemStatusFilter] = useState('open');
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [theme]);


  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('adminDashboard_activeTab', activeTab);
  }, [activeTab]);

  // Audit is exact-admin only
  useEffect(() => {
    if (activeTab === 'audit' && user && user.role !== 'admin') {
      setActiveTab('inventory');
    }
  }, [activeTab, user]);

  // Save selectedBrand to localStorage whenever it changes
  useEffect(() => {
    if (selectedBrand) {
      localStorage.setItem('adminDashboard_selectedBrand', selectedBrand);
    } else {
      localStorage.removeItem('adminDashboard_selectedBrand');
    }
  }, [selectedBrand]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/api/tenant/admin/missing-item-tickets/summary');
        if (cancelled) return;
        const openCount = res.data?.openCount || 0;
        setMissingItemOpenCount(openCount);
        if (openCount > 0 && !missingItemAlertDismissed) {
          setShowMissingItemAlert(true);
        }
      } catch (err) {
        console.error('Error fetching missing item ticket summary:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const fetchData = async () => {
    try {
      const [usersRes, sitesRes, brandsRes, invRes] = await Promise.all([
        axiosInstance.get('/api/tenant/users'),
        axiosInstance.get('/api/tenant/sites'),
        axiosInstance.get('/api/tenant/brands'),
        axiosInstance.get('/api/tenant/inventory')
      ]);
      setUsers(usersRes.data.users);
      setSites(sitesRes.data.sites);
      try {
        const statsRes = await axiosInstance.get('/api/tenant/admin/submission-stats');
        setSubmissionStats(statsRes.data);
      } catch (e) {
        const n = sitesRes.data.sites?.length || 0;
        setSubmissionStats({
          sitesSubmitted: 0,
          sitesPendingReview: 0,
          sitesNotSubmittedCount: 0,
          totalSites: n,
          periodSites: [],
          notSubmittedSites: []
        });
      }
      const fetchedBrands = brandsRes.data.brands || [];
      const fetchedSites = sitesRes.data.sites || [];
      const fetchedInv = invRes?.data?.items || [];
      setAllInventory(fetchedInv);
      // Only show brands that have sites or inventory (current imported brands)
      const activeBrandIds = new Set([
        ...fetchedSites.filter(s => s.brandId).map(s => s.brandId),
        ...fetchedInv.filter(i => i.brandId).map(i => i.brandId)
      ]);
      const activeBrands = fetchedBrands.filter(b => activeBrandIds.has(b.id));
      setBrands(activeBrands);
      
      // Check if saved brand still exists in active brands, otherwise use first active brand
      const savedBrandId = selectedBrand;
      const savedBrandExists = savedBrandId && activeBrands.some(b => b.id === savedBrandId);
      
      if (activeBrands.length > 0) {
        if (savedBrandExists) {
          await fetchInventoryForBrand(savedBrandId);
        } else {
          const firstBrandId = activeBrands[0].id;
          setSelectedBrand(firstBrandId);
          await fetchInventoryForBrand(firstBrandId);
        }
      } else if (savedBrandId) {
        setSelectedBrand(null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissionStats = async () => {
    try {
      const statsRes = await axiosInstance.get('/api/tenant/admin/submission-stats');
      setSubmissionStats(statsRes.data);
      setShowNotSubmittedPopup(true);
    } catch (e) {
      setSubmissionStats((prev) => ({
        ...prev,
        sitesSubmitted: 0,
        sitesPendingReview: 0,
        sitesNotSubmittedCount: 0,
        totalSites: sites.length || prev.totalSites,
        periodSites: [],
        notSubmittedSites: []
      }));
      setShowNotSubmittedPopup(true);
    }
  };

  const handleSendReminders = async () => {
    setReminderSending(true);
    setReminderMessage('');
    try {
      const res = await axiosInstance.post('/api/tenant/admin/send-reminders');
      setSubmissionStats(prev => ({ ...prev, reminderCooldownUntil: res.data.reminderCooldownUntil }));
      setReminderMessage('Reminder successfully sent to all managers / sites in the list');
    } catch (e) {
      if (e.response?.status === 429) {
        setSubmissionStats(prev => ({ ...prev, reminderCooldownUntil: e.response.data?.reminderCooldownUntil || null }));
      } else {
        setReminderMessage(e.response?.data?.error || 'Failed to send reminders');
      }
    } finally {
      setReminderSending(false);
    }
  };

  const fetchInventoryForBrand = async (brandId) => {
    try {
      const url = brandId ? `/api/tenant/inventory?brandId=${brandId}` : '/api/tenant/inventory';
      const response = await axiosInstance.get(url);
      setInventory(response.data.items);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      await axiosInstance.post('/api/tenant/users', userData);
      fetchData();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create user'
      };
    }
  };

  const handleCreateInventory = async (itemData) => {
    try {
      await axiosInstance.post('/api/tenant/inventory', itemData);
      fetchData();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create inventory item'
      };
    }
  };

  const handleCreateSite = async (siteData) => {
    try {
      await axiosInstance.post('/api/tenant/sites', siteData);
      fetchData();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create site'
      };
    }
  };

  const handleUpdateSite = async (siteId, siteData) => {
    try {
      await axiosInstance.put(`/api/tenant/sites/${siteId}`, siteData);
      fetchData();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update site'
      };
    }
  };

  const handleDeleteSite = async (siteId) => {
    try {
      await axiosInstance.delete(`/api/tenant/sites/${siteId}`);
      fetchData();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete site'
      };
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      await axiosInstance.put(`/api/tenant/users/${userId}`, userData);
      fetchData();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update user'
      };
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axiosInstance.delete(`/api/tenant/users/${userId}`);
      fetchData();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete user'
      };
    }
  };

  const currentParticipant = !user ? null : user.role === 'site' ? { type: 'site', id: user.siteId } : { type: 'user', id: user.id };

  const openChatWithUser = async (targetUser) => {
    try {
      const response = await axiosInstance.post('/api/tenant/chat/conversations', { recipientType: 'user', recipientId: targetUser.id });
      setChatConversationId(response.data.conversationId);
      setChatOtherPartyDisplayName(targetUser.name || targetUser.email || '');
      setShowChatModal(true);
    } catch (error) {
      console.error('Failed to open chat with user:', error);
    }
  };

  const openChatWithSite = async (site) => {
    try {
      const response = await axiosInstance.post('/api/tenant/chat/conversations', { recipientType: 'site', recipientId: site.id });
      setChatConversationId(response.data.conversationId);
      setChatOtherPartyDisplayName(site.name || site.email || '');
      setShowChatModal(true);
    } catch (error) {
      console.error('Failed to open chat with site:', error);
    }
  };

  const canStartChat = user?.role === 'admin';

  const sendPendingReviewReminder = async (siteId) => {
    if (!siteId) return;
    setPendingReviewRemindMessage('');
    setRemindingSiteId(siteId);
    try {
      await axiosInstance.post('/api/tenant/counts/remind-pending-count-review', { siteId });
      setPendingReviewRemindMessage('Reminder sent. The manager will see it in their notifications (chat).');
      setChatRefreshTrigger((t) => t + 1);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to send reminder';
      setPendingReviewRemindMessage(msg);
    } finally {
      setRemindingSiteId(null);
    }
  };

  const handleOpenStartNewPeriodModal = async () => {
    setArchiveMessage('');
    setPendingReviewRemindMessage('');
    setCheckingPendingReviews(true);
    try {
      const r = await axiosInstance.get('/api/tenant/counts/pending-manager-review-batches');
      const d = r.data || {};
      if (d.pendingBatchCount > 0) {
        setPendingReviewBlock({
          pendingBatchCount: d.pendingBatchCount,
          pendingBatches: Array.isArray(d.pendingBatches) ? d.pendingBatches : []
        });
        setShowPendingReviewBlockModal(true);
        return;
      }
    } catch (err) {
      setArchiveMessage(`Error: ${err.response?.data?.error || err.message || 'Failed to check pending count reviews'}`);
      setTimeout(() => setArchiveMessage(''), 6000);
      return;
    } finally {
      setCheckingPendingReviews(false);
    }
    setPeriodNameInput('');
    setShowPeriodNameModal(true);
  };

  const handleConfirmStartNewPeriod = async () => {
    setShowPeriodNameModal(false);
    setArchiving(true);
    setArchiveMessage('');
    const name = periodNameInput.trim().slice(0, 25);
    try {
      const response = await axiosInstance.post('/api/tenant/counts/archive-completed', name ? { periodName: name } : {});
      setArchiveMessage(`Success: ${response.data.message}`);
      setTimeout(() => {
        setArchiveMessage('');
        window.location.reload();
      }, 3000);
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 400 && data && data.pendingBatchCount > 0) {
        setPendingReviewRemindMessage('');
        setPendingReviewBlock({
          pendingBatchCount: data.pendingBatchCount,
          pendingBatches: Array.isArray(data.pendingBatches) ? data.pendingBatches : []
        });
        setShowPendingReviewBlockModal(true);
      } else {
        setArchiveMessage(`Error: ${data?.error || 'Failed to archive completed counts'}`);
        setTimeout(() => setArchiveMessage(''), 5000);
      }
    } finally {
      setArchiving(false);
    }
  };

  const handleFinanceExport = async () => {
    setFinanceExporting(true);
    setArchiveMessage('');
    try {
      const base = axiosInstance.defaults.baseURL || '';
      const url = base + '/api/tenant/reports/finance-export';
      const token = localStorage.getItem('token');
      const fetchHeaders = { Authorization: 'Bearer ' + token };
      if (typeof window !== 'undefined' && window.location.host) {
        fetchHeaders['X-Tenant-Host'] = window.location.host;
      }
      const res = await fetch(url, { headers: fetchHeaders });
      if (!res.ok) {
        let errData = null;
        try { errData = await res.json(); } catch (_) {}
        const msg =
          getFriendlyErrorMessage(
            { response: { status: res.status, data: errData || { code: 'EXPORT_FAILED' } } },
            { kind: 'export' }
          ) || EXPORT_FAILED_MESSAGE;
        setArchiveMessage(`Error: ${msg}`);
        setTimeout(() => setArchiveMessage(''), 8000);
        return;
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'Finance_Export_' + new Date().toISOString().split('T')[0] + '.xlsx';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error(err);
      setArchiveMessage(
        `Error: ${getFriendlyErrorMessage(err, { kind: 'export' }) || EXPORT_FAILED_MESSAGE}`
      );
      setTimeout(() => setArchiveMessage(''), 8000);
    } finally {
      setFinanceExporting(false);
    }
  };

  const fetchMissingItemSummary = async () => {
    try {
      const res = await axiosInstance.get('/api/tenant/admin/missing-item-tickets/summary');
      setMissingItemOpenCount(res.data?.openCount || 0);
    } catch (err) {
      console.error('Error fetching missing item ticket summary:', err);
    }
  };

  const loadMissingItemTickets = async (statusFilter = missingItemStatusFilter) => {
    setMissingItemTicketsLoading(true);
    try {
      const url = statusFilter && statusFilter !== 'all'
        ? `/api/tenant/admin/missing-item-tickets?status=${statusFilter}`
        : '/api/tenant/admin/missing-item-tickets';
      const res = await axiosInstance.get(url);
      setMissingItemTickets(res.data.tickets || []);
    } catch (err) {
      console.error('Error fetching missing item tickets:', err);
    } finally {
      setMissingItemTicketsLoading(false);
    }
  };

  const handleOpenMissingItemTicketsModal = () => {
    setShowMissingItemTicketsModal(true);
    loadMissingItemTickets(missingItemStatusFilter);
  };

  const handleMissingItemStatusFilterChange = (statusFilter) => {
    setMissingItemStatusFilter(statusFilter);
    loadMissingItemTickets(statusFilter);
  };

  const handleDismissMissingItemAlert = () => {
    setMissingItemAlertDismissed(true);
    setShowMissingItemAlert(false);
  };

  const handleViewMissingItemTickets = () => {
    setShowMissingItemAlert(false);
    handleOpenMissingItemTicketsModal();
  };

  const handleCloseMissingItemTicket = async (ticket, outcomeText) => {
    try {
      await axiosInstance.put(`/api/tenant/admin/missing-item-tickets/${ticket.id}/close`, {
        adminOutcome: outcomeText
      });
      await loadMissingItemTickets(missingItemStatusFilter);
      await fetchMissingItemSummary();
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Failed to close ticket');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  const tabLabel =
    activeTab === 'users'
      ? 'Users'
      : activeTab === 'sites'
        ? 'Sites'
        : activeTab === 'inventory'
          ? 'Inventory'
          : activeTab === 'archived'
            ? 'Archived Counts'
            : activeTab === 'import'
              ? 'Import'
              : activeTab === 'audit'
                ? 'Audit'
                : 'Reports';
  const breadcrumbSuffix =
    activeTab === 'audit' || activeTab === 'reports' ? '' : ' Management';
  return (
    <div className={'app-layout ' + (isSidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded')}>
      <Navbar user={user} onLogout={logout} adminNav={{ activeTab, setActiveTab }} isSidebarCollapsed={isSidebarCollapsed} onSidebarCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} onOpenSupport={() => setShowSupportModal(true)} />
      <div className="dashboard">
        <header className="admin-header">
          <div className="admin-header-left">
            <button type="button" className="admin-header-sidebar-toggle" onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <ChevronRight size={20} style={{ transform: isSidebarCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>
            <span className="admin-header-breadcrumb">
              Admin &gt; <span>{tabLabel}{breadcrumbSuffix}</span>
            </span>
          </div>
          <div className="admin-header-right">
            <button type="button" className="admin-header-icon-btn" onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))} aria-label="Toggle theme">
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <ChatBell
              onSelectConversation={(id, name) => {
                setChatConversationId(id);
                setChatOtherPartyDisplayName(name);
                setShowChatModal(true);
              }}
              buttonClassName="admin-header-icon-btn"
              iconSize={20}
              userRole={user?.role}
              refreshTrigger={chatRefreshTrigger}
            />
            <button type="button" className="admin-header-signout" onClick={logout}>
              <LogOut size={18} />
              <span className="hidden-sm">Sign Out</span>
            </button>
          </div>
        </header>
        <div className="dashboard-header-wrap">
          <div className="dashboard-header-card card">
            <h2>Admin Dashboard</h2>
            <p>Manage inventory, sites, and users</p>
          </div>
        </div>

        <div className="admin-tabs-mobile tabs">
          <button type="button" className={`tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>Users</button>
          <button type="button" className={`tab ${activeTab === 'sites' ? 'active' : ''}`} onClick={() => setActiveTab('sites')}>Sites</button>
          <button type="button" className={`tab ${activeTab === 'inventory' ? 'active' : ''}`} onClick={() => setActiveTab('inventory')}>Inventory</button>
          <button type="button" className={`tab ${activeTab === 'archived' ? 'active' : ''}`} onClick={() => setActiveTab('archived')}>Archived Counts</button>
          <button type="button" className={`tab ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>Reports</button>
          <button type="button" className={`tab ${activeTab === 'import' ? 'active' : ''}`} onClick={() => setActiveTab('import')}>Import</button>
          {user?.role === 'admin' && (
            <button type="button" className={`tab ${activeTab === 'audit' ? 'active' : ''}`} onClick={() => setActiveTab('audit')}>Audit</button>
          )}
        </div>

        {activeTab === 'inventory' && (
          <>
            <div className="card mit-inventory-entry">
              <div className="mit-inventory-entry-left">
                <PackageMinus size={20} className="mit-banner-icon" aria-hidden />
                <div>
                  <div className="mit-inventory-entry-title">
                    Inventory Tickets
                    {missingItemOpenCount > 0 && (
                      <span className="mit-open-badge" aria-label={`${missingItemOpenCount} open`}>
                        {missingItemOpenCount}
                      </span>
                    )}
                  </div>
                  {missingItemOpenCount > 0 ? (
                    <p className="mit-inventory-entry-sub">
                      There {missingItemOpenCount === 1 ? 'is' : 'are'} {missingItemOpenCount} open ticket{missingItemOpenCount === 1 ? '' : 's'} to action.
                    </p>
                  ) : (
                    <p className="mit-inventory-entry-sub">View open and closed inventory tickets.</p>
                  )}
                </div>
              </div>
              <button type="button" className="btn btn-primary mit-banner-btn" onClick={handleOpenMissingItemTicketsModal}>
                View tickets
              </button>
            </div>
            <div className="me-section">
              <button type="button" className="me-toggle-btn" onClick={() => setMeOpen(!meOpen)}>
                <div>
                  <h2 className="me-section-title">Month End Management</h2>
                  <p className="me-section-subtitle">Archive counts and export finance reports</p>
                </div>
                <ChevronDown size={16} className={'me-toggle-icon' + (meOpen ? ' me-toggle-open' : '')} />
              </button>
              {meOpen && <div className="me-content-row">
                <div className="me-actions-col">
                  <div className="me-action-block">
                    <button
                      className="btn btn-primary me-btn-archive"
                      onClick={handleOpenStartNewPeriodModal}
                      disabled={archiving || checkingPendingReviews}
                    >
                      {archiving ? 'Archiving...' : checkingPendingReviews ? 'Checking…' : 'Start New Counting Period'}
                    </button>
                    {archiveMessage && (
                      <div className={`me-message ${archiveMessage.startsWith('Error') ? 'me-message-error' : 'me-message-success'}`}>
                        {archiveMessage}
                      </div>
                    )}
                    <p className="me-help-text">
                      This will archive all completed counts from managers and clear them from the system, starting a new counting period.
                    </p>
                  </div>
                  <div className="me-action-block">
                    <button
                      className="btn btn-secondary"
                      onClick={handleFinanceExport}
                      disabled={financeExporting}
                    >
                      {financeExporting ? 'Exporting...' : 'Finance Export'}
                    </button>
                    <p className="me-help-text">
                      This will export a finance report including inventory data from all archived counting periods only. Data from the current active period is not included.
                    </p>
                  </div>
                </div>
                <div className="me-stat-card" onClick={fetchSubmissionStats} onKeyDown={(e) => e.key === 'Enter' && fetchSubmissionStats()} role="button" tabIndex={0}>
                  <span className="me-stat-label">CURRENT PERIOD</span>
                  <span className="me-stat-value">{submissionStats.sitesSubmitted} of {submissionStats.totalSites} Sites</span>
                </div>
              </div>}
            </div>
            {showPendingReviewBlockModal && (
              <div
                className="me-period-modal-overlay"
                onClick={() => {
                  setShowPendingReviewBlockModal(false);
                  setPendingReviewRemindMessage('');
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="me-pending-review-modal-title"
              >
                <div className="card me-period-modal me-pending-review-modal" onClick={(e) => e.stopPropagation()}>
                  <h3 id="me-pending-review-modal-title" className="me-period-modal-title me-pending-review-modal-title">
                    Cannot start a new counting period
                  </h3>
                  <p className="me-period-modal-desc">
                    There {pendingReviewBlock.pendingBatchCount === 1 ? 'is' : 'are'}{' '}
                    <strong>{pendingReviewBlock.pendingBatchCount}</strong> count batch
                    {pendingReviewBlock.pendingBatchCount === 1 ? '' : 'es'} awaiting manager review (pending status). Resolve
                    these on the manager dashboard before starting a new period.
                  </p>
                  <div className="me-pending-review-list" role="list">
                    {(pendingReviewBlock.pendingBatches || []).map((row, idx) => {
                      const canRemind = Boolean(row.managerId);
                      const isSending = remindingSiteId === row.siteId;
                      return (
                        <div
                          key={`${row.siteId || 'site'}-${idx}`}
                          className="me-pending-review-card"
                          role="listitem"
                        >
                          <div className="me-pending-review-field me-pending-review-field--grow">
                            <span className="me-pending-review-label">Site</span>
                            <span className="me-pending-review-value" title={row.siteName || undefined}>
                              {row.siteName || '—'}
                            </span>
                          </div>
                          <div className="me-pending-review-field me-pending-review-field--grow">
                            <span className="me-pending-review-label">Area Manager</span>
                            <span className="me-pending-review-value" title={row.areaManagerName || undefined}>
                              {row.areaManagerName || '—'}
                            </span>
                          </div>
                          <div className="me-pending-review-card-action">
                            <button
                              type="button"
                              className="btn btn-secondary me-pending-review-icon-btn"
                              disabled={!canRemind || isSending}
                              aria-busy={isSending}
                              aria-label={isSending ? 'Sending reminder' : 'Send reminder'}
                              title={canRemind ? 'Sends an in-app chat message; manager sees it in notifications' : 'No area manager assigned to this site'}
                              onClick={() => sendPendingReviewReminder(row.siteId)}
                            >
                              {isSending ? (
                                <Loader2 className="me-pending-review-send-spin" size={18} aria-hidden />
                              ) : (
                                <Send size={18} aria-hidden />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {pendingReviewRemindMessage && (
                    <p className={'me-pending-review-feedback ' + (pendingReviewRemindMessage.startsWith('Reminder sent') ? 'me-pending-review-feedback--ok' : 'me-pending-review-feedback--err')}>
                      {pendingReviewRemindMessage}
                    </p>
                  )}
                  <div className="me-period-modal-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setShowPendingReviewBlockModal(false);
                        setPendingReviewRemindMessage('');
                      }}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}
            {showPeriodNameModal && (
              <div className="me-period-modal-overlay" onClick={() => setShowPeriodNameModal(false)} role="dialog" aria-modal="true" aria-labelledby="me-period-modal-title">
                <div className="card me-period-modal" onClick={(e) => e.stopPropagation()}>
                  <h3 id="me-period-modal-title" className="me-period-modal-title">Name this counting period</h3>
                  <p className="me-period-modal-desc">Give the archived period a custom name (e.g. January.26). Leave blank to use the default date-based name.</p>
                  <input
                    type="text"
                    className="form-control me-period-modal-input"
                    placeholder="e.g. January.26"
                    maxLength={25}
                    value={periodNameInput}
                    onChange={(e) => setPeriodNameInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleConfirmStartNewPeriod()}
                    autoFocus
                  />
                  <div className="me-period-modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowPeriodNameModal(false)}>Cancel</button>
                    <button type="button" className="btn btn-primary" onClick={handleConfirmStartNewPeriod}>Confirm &amp; Start New Period</button>
                  </div>
                </div>
              </div>
            )}
            {showNotSubmittedPopup && (() => {
              const siteRows =
                submissionStats.periodSites && submissionStats.periodSites.length > 0
                  ? submissionStats.periodSites
                  : submissionStats.notSubmittedSites || [];
              const remindableCount = (submissionStats.notSubmittedSites || []).length;
              const statusBadgeStyle = (status) => {
                if (status === 'Submitted') {
                  return { padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', backgroundColor: '#d1fae5', color: '#047857' };
                }
                if (status === 'Pending Review') {
                  return { padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', backgroundColor: '#fff3cd', color: '#856404' };
                }
                return { padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', backgroundColor: '#f8d7da', color: '#721c24' };
              };
              return (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowNotSubmittedPopup(false)}>
                <div className="card" style={{ maxWidth: '700px', width: '90%', maxHeight: '80vh', overflow: 'auto', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={{ margin: 0 }}>Current period — all sites ({siteRows.length})</h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleSendReminders}
                        disabled={remindableCount === 0 || reminderSending || (submissionStats.reminderCooldownUntil && new Date() < new Date(submissionStats.reminderCooldownUntil))}
                      >
                        {reminderSending ? 'Sending...' : 'Send Reminder'}
                      </button>
                      <button className="btn btn-sm btn-secondary" onClick={() => { setReminderMessage(''); setShowNotSubmittedPopup(false); }}>Close</button>
                    </div>
                  </div>
                  {reminderMessage && <p style={{ marginBottom: '15px', fontWeight: 'bold', color: reminderMessage.startsWith('Reminder successfully') ? '#28a745' : '#dc3545' }}>{reminderMessage}</p>}
                  <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', marginBottom: '15px' }}>
                    {submissionStats.periodSites && submissionStats.periodSites.length > 0 ? (
                      <>
                        <strong>{submissionStats.sitesSubmitted}</strong> submitted,{' '}
                        <strong>{submissionStats.sitesPendingReview}</strong> pending manager review,{' '}
                        <strong>{submissionStats.sitesNotSubmittedCount}</strong> not yet submitted ({submissionStats.totalSites}{' '}
                        sites).
                      </>
                    ) : (
                      <>
                        {submissionStats.sitesSubmitted} of {submissionStats.totalSites} sites have completed counts this
                        period.
                      </>
                    )}
                  </p>
                  {siteRows.length === 0 ? (
                    <p style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>No sites found.</p>
                  ) : (
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Site Name</th>
                            <th>Manager</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {siteRows.map((site) => (
                            <tr key={site.siteId}>
                              <td>{site.siteName}</td>
                              <td>{site.managerName}</td>
                              <td>
                                <span style={statusBadgeStyle(site.status)}>
                                  {site.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              );
            })()}
            <div className="card" style={{ marginBottom: '20px' }}>
              <div className="card-header">
                <h3>Select Brand</h3>
              </div>
              <div style={{ padding: '15px' }}>
                {brands.length === 0 ? (
                  <div style={{ padding: '10px', background: '#fff3cd', borderRadius: '4px' }}>
                    <p style={{ margin: 0, color: '#856404' }}>
                      <strong>No brands available.</strong> Please create a brand in the Sites tab before managing inventory.
                    </p>
                  </div>
                ) : (
                  <>
                    <select
                      value={selectedBrand || ''}
                      onChange={(e) => {
                        const brandId = e.target.value;
                        setSelectedBrand(brandId);
                        fetchInventoryForBrand(brandId);
                      }}
                      required
                      style={{ padding: '8px', fontSize: '16px', minWidth: '200px' }}
                    >
                      {brands.map(brand => (
                        <option key={brand.id} value={brand.id}>
                          {brand.name}
                        </option>
                      ))}
                    </select>
                    <p style={{ marginTop: '10px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
                      {selectedBrand && `Showing inventory for: ${brands.find(b => b.id === selectedBrand)?.name || 'Selected Brand'}`}
                    </p>
                  </>
                )}
              </div>
            </div>
            {selectedBrand && (
              <InventoryManagement
                inventory={inventory.filter(item => item.brandId === selectedBrand)}
                allInventory={allInventory}
                onCreateItem={handleCreateInventory}
                onRefresh={() => fetchInventoryForBrand(selectedBrand)}
                selectedBrand={selectedBrand}
                brands={brands}
                sites={sites}
                isAdmin={user?.role === 'admin'}
              />
            )}
            {!selectedBrand && brands.length > 0 && (
              <div className="card">
                <p style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                  Please select a brand to view and manage inventory.
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === 'sites' && (
          <SiteManagement
            sites={sites}
            users={users.filter(u => u.role === 'manager')}
            onCreateSite={handleCreateSite}
            onUpdateSite={handleUpdateSite}
            onDeleteSite={handleDeleteSite}
            onRefresh={fetchData}
            allBrands={brands}
            onChatSite={openChatWithSite}
            canStartChat={canStartChat}
            isAdmin={user?.role === 'admin'}
          />
        )}

        {activeTab === 'users' && (
          <UserManagement
            users={users}
            onCreateUser={handleCreateUser}
            onUpdateUser={handleUpdateUser}
            onDeleteUser={handleDeleteUser}
            canCreatePowerUser={false}
            onRefresh={fetchData}
            onChatUser={openChatWithUser}
            canStartChat={canStartChat}
            isAdmin={user?.role === 'admin'}
          />
        )}

        {activeTab === 'archived' && (
          <ArchivedCounts />
        )}

        {activeTab === 'reports' && (
          <Reports sites={sites} users={users} brands={brands} inventory={allInventory} />
        )}

        {activeTab === 'import' && (
          <ImportSection brands={brands} onRefresh={fetchData} />
        )}

        {activeTab === 'audit' && user?.role === 'admin' && (
          <AuditLog />
        )}

        {showChatModal && chatConversationId && (
          <ChatModal
            conversationId={chatConversationId}
            otherPartyDisplayName={chatOtherPartyDisplayName}
            onClose={() => {
              setShowChatModal(false);
              setChatConversationId(null);
              setChatOtherPartyDisplayName('');
              setChatRefreshTrigger(t => t + 1);
            }}
            currentParticipant={currentParticipant}
            onDeleteConversation={() => {
              setShowChatModal(false);
              setChatConversationId(null);
              setChatOtherPartyDisplayName('');
              setChatRefreshTrigger(t => t + 1);
            }}
          />
        )}
        <SupportModal
          open={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          tenantName={typeof window !== 'undefined' ? window.location.hostname : ''}
        />

        {showMissingItemAlert && (
          <div className="me-period-modal-overlay" onClick={handleDismissMissingItemAlert} role="dialog" aria-modal="true" aria-labelledby="mit-alert-modal-title">
            <div className="card me-period-modal" onClick={(e) => e.stopPropagation()}>
              <h3 id="mit-alert-modal-title" className="me-period-modal-title">Inventory Tickets</h3>
              <p className="me-period-modal-desc">
                There {missingItemOpenCount === 1 ? 'is' : 'are'} <strong>{missingItemOpenCount}</strong> open inventory ticket{missingItemOpenCount === 1 ? '' : 's'} to action.
              </p>
              <div className="me-period-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={handleDismissMissingItemAlert}>Dismiss</button>
                <button type="button" className="btn btn-primary" onClick={handleViewMissingItemTickets}>View tickets</button>
              </div>
            </div>
          </div>
        )}

        {showMissingItemTicketsModal && (
          <div className="me-period-modal-overlay" onClick={() => setShowMissingItemTicketsModal(false)} role="dialog" aria-modal="true" aria-labelledby="mit-manage-modal-title">
            <div className="card me-period-modal mit-manage-modal" onClick={(e) => e.stopPropagation()}>
              <div className="mit-manage-modal-header">
                <h3 id="mit-manage-modal-title" className="me-period-modal-title">Inventory Tickets</h3>
                <button type="button" className="mit-manage-modal-close" onClick={() => setShowMissingItemTicketsModal(false)} aria-label="Close">&times;</button>
              </div>
              <div className="mit-manage-filters">
                {['open', 'closed', 'all'].map((filterValue) => (
                  <button
                    key={filterValue}
                    type="button"
                    className={`mit-manage-filter-btn${missingItemStatusFilter === filterValue ? ' active' : ''}`}
                    onClick={() => handleMissingItemStatusFilterChange(filterValue)}
                  >
                    {filterValue === 'open' ? 'Open' : filterValue === 'closed' ? 'Closed' : 'All'}
                  </button>
                ))}
              </div>
              <div className="mit-manage-modal-body">
                <MissingItemTicketsPanel
                  tickets={missingItemTickets}
                  mode="admin"
                  loading={missingItemTicketsLoading}
                  onRefresh={() => loadMissingItemTickets(missingItemStatusFilter)}
                  onCloseTicket={handleCloseMissingItemTicket}
                  photoBasePath="/api/tenant/admin/missing-item-tickets"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
